import { FieldErrors, FieldValues } from 'react-hook-form';

import { StringKey } from '../../../lang/index';
import { getTranslation } from '../../../utils/get-translation.util';

// React Hook Form keeps these on an error node next to the nested field errors — skip them so the
// walk below reports the field that failed, not the bookkeeping around it.
const ERROR_NODE_META_KEYS = ['message', 'ref', 'root', 'type', 'types'];

const isErrorLeaf = (value: unknown): value is { message: string } =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as { message?: unknown }).message === 'string' &&
  !!(value as { message: string }).message;

const findFirstErrorPath = (node: unknown, path: string[] = []): null | string[] => {
  if (!node || typeof node !== 'object') return null;

  const nestedPath = Object.entries(node)
    .filter(([key]) => !ERROR_NODE_META_KEYS.includes(key))
    .reduce<null | string[]>(
      (found, [key, value]) => found ?? findFirstErrorPath(value, [...path, key]),
      null,
    );

  if (nestedPath) return nestedPath;

  return isErrorLeaf(node) && path.length > 0 ? path : null;
};

// Suffixes, longest match first: the same leaf name means different things per event type
// (`date` is the round's opening date but a transaction date under `buyBackItems`), so a bare
// leaf-name map would mislabel the toast.
const FIELD_LABEL_KEY_BY_PATH_SUFFIX: [string, StringKey][] = [
  ['fundraising-round.event.sharePrice', 'SHARE_PRICE'],
  ['fundraising-round.investmentItems.investmentDate', 'INVESTMENT_DATE'],
  ['fundraising-round.investmentItems.stakeholder', 'STAKEHOLDER'],
  ['fundraising-round.investmentItems.shareClass', 'SHARE_CLASS'],
  ['fundraising-round.investmentItems.investment', 'INVESTMENT'],
  ['fundraising-round.investmentItems.files', 'FILES'],
  ['fundraising-round.investmentItems', 'INVESTMENT'],
  ['fundraising-round.roundingSetting', 'ROUNDING_SETTINGS'],
  ['fundraising-round.closeDate', 'CLOSING_DATE'],
  ['fundraising-round.date', 'OPEN_DATE'],
  ['fundraising-round.event', 'ROUND_NAME'],
  ['share-issuance.event.sharePrice', 'SHARE_PRICE'],
  ['share-issuance.event', 'EVENT_NAME'],
  ['stepTwo.additionalNotes', 'ADDITIONAL_NOTES'],
  ['stepTwo.files', 'FILES'],
];

const normalizeErrorPath = (path: string[]): string[] =>
  path.filter((segment) => !/^\d+$/.test(segment));

const findLabelKey = (path: string[]): StringKey | undefined =>
  FIELD_LABEL_KEY_BY_PATH_SUFFIX.find(([suffix]) => path.join('.').endsWith(suffix))?.[1];

// Walk the path back one segment at a time so a leaf with no label of its own resolves to the
// field that owns it — `event.id` is labelled by its `event` object, which is what the user sees.
const resolveLabelKey = (path: string[]): StringKey | undefined =>
  path.length === 0 ? undefined : (findLabelKey(path) ?? resolveLabelKey(path.slice(0, -1)));

// The edit wizard validates every step at once, so a failing field is often on a step the user
// cannot see — and `event.id` has no rendered error at all. Naming the field in the toast keeps
// "fix the highlighted fields" from pointing at nothing.
export const describeInvalidFormField = <T extends FieldValues>(
  errors: FieldErrors<T>,
): string | undefined => {
  const errorPath = findFirstErrorPath(errors);
  if (!errorPath) return undefined;

  const labelKey = resolveLabelKey(normalizeErrorPath(errorPath));

  return labelKey ? getTranslation(labelKey) : undefined;
};
